import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import styles from "./LoyaltyRewardBrandRefresh.module.css";
import legacyStyles from "./LoyaltyReward.module.css";
import hourglassIcon from "./imgs/rewards-hourglass.svg";
import lockIcon from "./imgs/rewards-lock.svg";
import infoIcon from "./imgs/rewards-info.svg";
import RewardsTitleModal from "./RewardsTitleModal";
import BrandRefreshTooltip from "../../components/CustomProductOption/BrandRefreshTooltip";
import { isBrandRefreshEnabled } from "../../shared/utilities/brandRefresh";
import { getTimeRemaining } from "../../shared/utilities/dateTime";

const formatExpiry = (expiredAt, brandRefresh) => {
  if (!expiredAt) return "";
  if (!brandRefresh) return getTimeRemaining(expiredAt);
  const seconds = Math.max(0, Math.floor((new Date(expiredAt).getTime() - Date.now()) / 1000));
  if (!Number.isFinite(seconds)) return "";
  const days = Math.floor(seconds / 86400);
  if (days > 0) return `${days} ${days === 1 ? "day" : "days"}`;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h : ${minutes}m`;
  return `${minutes}m : ${seconds % 60}s`;
};

function ProductCard({ id, image, title, description, expiredAt, link, pointsRemaining, openDeliveryWidget }) {
  const brandRefresh = isBrandRefreshEnabled();
  const titleElement = useRef(null);
  const [titleOverflows, setTitleOverflows] = useState(false);
  const [showFullTitle, setShowFullTitle] = useState(false);
  const closeFullTitle = useCallback(() => setShowFullTitle(false), []);
  useEffect(() => {
    if (!brandRefresh) return undefined;
    let mounted = true;
    const measure = () => {
      if (!mounted || !titleElement.current || !document.createRange) return;
      const element = titleElement.current;
      const range = document.createRange();
      range.selectNodeContents(element);
      if (!range.getClientRects) return;
      const rectangles = Array.from(range.getClientRects());
      const lines = rectangles.reduce((tops, rectangle) => {
        if (!tops.some(top => Math.abs(top - rectangle.top) < 1)) tops.push(rectangle.top);
        return tops;
      }, []);
      setTitleOverflows(lines.length > 2 || rectangles.some(rectangle => rectangle.width > element.clientWidth + 1));
    };
    measure();
    window.addEventListener("resize", measure);
    if (document.fonts) {
      document.fonts.ready.then(measure);
      if (document.fonts.addEventListener) document.fonts.addEventListener("loadingdone", measure);
    }
    return () => {
      mounted = false;
      window.removeEventListener("resize", measure);
      if (document.fonts && document.fonts.removeEventListener) document.fonts.removeEventListener("loadingdone", measure);
    };
  }, [title, brandRefresh]);
  const legacyExpiry = useRef(expiredAt);
  const [time, setTime] = useState(() => brandRefresh ? formatExpiry(expiredAt, true) : "");
  useEffect(() => {
    if (!brandRefresh) return undefined;
    const refreshTime = () => setTime(formatExpiry(expiredAt, brandRefresh));
    refreshTime();
    const interval = setInterval(refreshTime, 1000);
    return () => clearInterval(interval);
  }, [expiredAt, brandRefresh]);
  useEffect(() => {
    if (brandRefresh) return undefined;
    const interval = setInterval(() => {
      if (legacyExpiry.current) setTime(getTimeRemaining(legacyExpiry.current));
    }, 1000);
    return () => clearInterval(interval);
  }, [brandRefresh]);

  const renderLegacy = () => (
    <div
      id={`product_${id}`}
      className={`w-[343px] lg:w-[276px] h-[192px] lg:h-[315px] rounded-[20px] border-4 
      ${
        link
          ? "border-[#FF70A8]"
          : "border-darkDry"
      } pt-6 px-[10px] pb-4 flex flex-col gap-1 items-center
        transition-width transition-slowest ease`}
    >
      {time && (
        <div
          className={`${legacyStyles.expiredTime} bg-border w-[200px] h-5 -mt-6 rounded-[4px] flex items-center justify-center
        py-[2px] px-2
        text-sm leading-4 text-white font-montserratBold font-bold`}
        >
          Ends in 
          {' '}
          {time}
        </div>
      )}
      <div className="flex flex-row lg:flex-col gap-3 items-center">
        <img
          src={image}
          alt={title}
          className={`${link ? "" : legacyStyles.lockedProduct} w-36 h-36`}
        />
        <div className="flex flex-col lg:items-center lg:w-full w-[191px] gap-3">
          <div className="flex flex-col lg:items-center h-full lg:h-16 ">
            <p className={`${link ? 'text-white' : 'text-darkElevationPrimary'} 
              text-xl leading-6 tracking-[-0.3px] font-montserratBold font-bold lg:text-center`}
            >
              {title}
            </p>
            <p className="text-sm leading-4 font-montserratMedium text-[#B4A5BB]">
              {description}
            </p>
          </div>

          <div className="h-[52px] w-[95px] lg:hidden">
            {link ? (
              <button
                onClick={() => openDeliveryWidget(link, id)}
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    openDeliveryWidget(link, id)
                  }
                }}
                type="button"
                className="
          bg-button text-lg leading-[22px] tracking-[-0.1px] 
          font-montserratBold text-white h-[52px] w-[95px] rounded-[40px]"
              >
                Get It!
              </button>
            ) : (
              <button
                type="button"
                className="
          bg-darkElevationPrimary text-lg leading-[22px] tracking-[-0.1px] 
          font-montserratBold text-white h-[52px] w-[95px] rounded-[40px]"
              >
                Locked
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="hidden lg:block h-[52px] w-[95px]">
        {link ? (
          <button
            onClick={() => openDeliveryWidget(link, id)}
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === "Enter") {
                openDeliveryWidget(link, id)
              }
            }}
            type="button"
            className="
    bg-button text-white text-lg leading-[22px] tracking-[-0.1px] 
    font-montserratBold h-[52px] w-[95px] rounded-[40px]"
          >
            Get It!
          </button>
        ) : (
          <button
            type="button"
            className="
      bg-darkElevationPrimary text-lg leading-[22px] tracking-[-0.1px] 
      font-montserratBold text-white h-[52px] w-[95px] rounded-[40px]"
          >
            Locked
          </button>
        )}
      </div>
    </div>
  );

  const renderAction = () => (
    <button
      type="button"
      className={styles.redeemButton}
      disabled={!link}
      onClick={() => openDeliveryWidget(link, id)}
    >
      {link ? "Get it" : "Locked"}
    </button>
  );

  if (!brandRefresh) return renderLegacy();

  return (
    <div id={`product_${id}`} className={styles.productCard}>
      <div className={styles.productTitleContainer}>
        <p ref={titleElement} className={`${styles.productTitle} ${titleOverflows ? styles.truncatedTitle : ""}`}>{title}</p>
      </div>
      {titleOverflows && (
        <span className={styles.titleInfoWrapper}>
          <button
            type="button"
            className={styles.titleInfoButton}
            aria-label={`Show full offer title: ${title}`}
            onClick={() => {
              if (window.matchMedia && window.matchMedia("(max-width: 767px)").matches) setShowFullTitle(true);
            }}
          >
            <img src={infoIcon} alt="" aria-hidden="true" width="20" height="20" />
          </button>
          <BrandRefreshTooltip description={title} />
        </span>
      )}
      {showFullTitle && <RewardsTitleModal title={title} titleId={`offer-title-${id}`} onClose={closeFullTitle} />}
      <img className={styles.productImage} src={image} alt={title} />
      <div className={`${styles.productFooter} ${!link && pointsRemaining !== null ? styles.lockedFooter : ""}`}>
        {time && (
          <div className={styles.expiredTime}>
            <img className={styles.rewardIcon} src={hourglassIcon} alt="" aria-hidden="true" width="16" height="16" />
            <span>
              Ends in
              {' '}
              <strong>{time}</strong>
            </span>
          </div>
        )}
        {!link && pointsRemaining !== null ? (
          <span className={styles.lockedReward}>
            <img className={styles.rewardIcon} src={lockIcon} alt="" aria-hidden="true" width="21" height="21" />
            {pointsRemaining.toLocaleString("en-US")}
            {' '}
            pts away
          </span>
        ) : <div className={styles.productAction}>{renderAction()}</div>}
      </div>
    </div>
  );
}

ProductCard.propTypes = {
  image: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  expiredAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  link: PropTypes.string,
  pointsRemaining: PropTypes.number,
  openDeliveryWidget: PropTypes.func.isRequired
};
ProductCard.defaultProps = { expiredAt: null, link: null, description: "", pointsRemaining: null };
export default ProductCard;
